import {
  Package,
  ShoppingCart,
  Store,
  Users,
  CircleDollarSign,
} from 'lucide-react';
import {
  MetricCard,
  MetricCardSkeletonGrid,
  SectionHeader,
} from '@/components/dashboard/DashboardCards';
import {
  formatCount,
  formatMoneyCompact,
  statCount,
} from '@/components/dashboard/commandCenterConfig';
import { routes } from '@/routes/paths';
import type { OrderStatistics } from '@/types/dashboard';

export function BusinessSnapshot({
  stats,
  grossSale,
  loading,
  onOpenSales,
}: {
  stats: OrderStatistics | undefined;
  grossSale: number | null;
  loading: boolean;
  onOpenSales: () => void;
}) {
  const orders = statCount(stats, 'total_orders');
  const items = statCount(stats, 'total_items');
  const stores = statCount(stats, 'total_stores');
  const customers = statCount(stats, 'total_customers');
  const newOrders = statCount(stats, 'new_orders');
  const newItems = statCount(stats, 'new_items');
  const newStores = statCount(stats, 'new_stores');
  const newCustomers = statCount(stats, 'new_customers');

  return (
    <section className="cmd-section" aria-label="Business snapshot">
      <SectionHeader
        title="Business Snapshot"
      />
      {loading && !stats ? (
        <MetricCardSkeletonGrid count={5} />
      ) : (
        <div className="kpi-strip">
          <MetricCard
            loading={loading}
            icon={<ShoppingCart size={22} strokeWidth={1.85} />}
            label="Orders"
            value={formatCount(orders)}
            subtitle={newOrders != null ? `${formatCount(newOrders)} newly added` : 'All customer orders'}
            to={routes.orders}
            search={{ status: 'all', page: 1, search: '' }}
            tone="green"
          />
          <MetricCard
            loading={loading}
            icon={<CircleDollarSign size={22} strokeWidth={1.85} />}
            label="Gross Sales"
            value={formatMoneyCompact(grossSale)}
            subtitle="Revenue in selected period"
            onOpen={onOpenSales}
            tone="teal"
          />
          <MetricCard
            loading={loading}
            icon={<Store size={22} strokeWidth={1.85} />}
            label="Stores"
            value={formatCount(stores)}
            subtitle={newStores != null ? `${formatCount(newStores)} newly added` : 'Active stores'}
            to={routes.stores}
            tone="green"
          />
          <MetricCard
            loading={loading}
            icon={<Users size={22} strokeWidth={1.85} />}
            label="Customers"
            value={formatCount(customers)}
            subtitle={newCustomers != null ? `${formatCount(newCustomers)} newly added` : 'Registered customers'}
            to={routes.customers}
            tone="green"
          />
          <MetricCard
            loading={loading}
            icon={<Package size={22} strokeWidth={1.85} />}
            label="Items"
            value={formatCount(items)}
            subtitle={newItems != null ? `${formatCount(newItems)} newly added` : 'Approved catalog items'}
            to={routes.items}
            tone="amber"
            wide
          />
        </div>
      )}
    </section>
  );
}
